import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { changePassword, updateCurrentUser } from '@/features/auth/api'
import { useAuth } from '@/features/auth/store'
import { toast } from '@/lib/toast'

const detailsSchema = z.object({
  full_name: z.string().trim().min(1, 'Enter your name').max(255, 'Name is too long'),
  phone: z.string().trim().max(30, 'Phone number is too long').optional().or(z.literal('')),
})

const passwordSchema = z
  .object({
    current_password: z.string().min(1, 'Enter your current password'),
    new_password: z.string().min(8, 'Use at least 8 characters'),
    confirm_password: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

type DetailsFormValues = z.infer<typeof detailsSchema>
type PasswordFormValues = z.infer<typeof passwordSchema>

export function EditProfile() {
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const reload = useAuth((s) => s.reload)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const detailsForm = useForm<DetailsFormValues>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      full_name: user?.full_name ?? '',
      phone: user?.phone ?? '',
    },
  })

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  })

  if (!user) return null

  const initials = user.full_name
    .split(' ')
    .map((name) => name[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

  const onSubmitDetails = async (data: DetailsFormValues) => {
    setDetailsError(null)
    try {
      await updateCurrentUser({
        full_name: data.full_name,
        phone: data.phone || null,
      })
      await reload()
      detailsForm.reset({ full_name: data.full_name, phone: data.phone })
      toast.success('Profile details updated')
    } catch (error) {
      setDetailsError(error instanceof ApiError ? error.message : 'Failed to update profile')
    }
  }

  const onSubmitPassword = async (data: PasswordFormValues) => {
    setPasswordError(null)
    try {
      await changePassword(data.current_password, data.new_password)
      passwordForm.reset()
      toast.success('Password changed successfully')
    } catch (error) {
      setPasswordError(error instanceof ApiError ? error.message : 'Failed to change password')
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Edit Profile</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{user.email}</p>
        </div>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back
        </Button>
      </header>

      <Card>
        <CardHeader title="Profile" description="Your name and contact details." />
        <CardBody className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 text-xl font-bold text-brand-700 shadow-sm dark:bg-brand-900/60 dark:text-brand-300">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {user.full_name}
              </p>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                {user.roles.map((role) => role.name).join(', ') || 'No role assigned'}
              </p>
            </div>
          </div>

          <form onSubmit={detailsForm.handleSubmit(onSubmitDetails)} className="space-y-4">
            {detailsError && <Alert tone="error">{detailsError}</Alert>}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full Name" error={detailsForm.formState.errors.full_name?.message}>
                {(fieldProps) => (
                  <Input
                    {...fieldProps}
                    {...detailsForm.register('full_name')}
                    autoComplete="name"
                    disabled={detailsForm.formState.isSubmitting}
                  />
                )}
              </Field>
              <Field label="Phone Number" error={detailsForm.formState.errors.phone?.message}>
                {(fieldProps) => (
                  <Input
                    {...fieldProps}
                    {...detailsForm.register('phone')}
                    autoComplete="tel"
                    disabled={detailsForm.formState.isSubmitting}
                  />
                )}
              </Field>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                loading={detailsForm.formState.isSubmitting}
                disabled={!detailsForm.formState.isDirty}
              >
                Save Details
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Password" description="Change the password for this account." />
        <CardBody>
          <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
            {passwordError && <Alert tone="error">{passwordError}</Alert>}
            <Field
              label="Current Password"
              error={passwordForm.formState.errors.current_password?.message}
            >
              {(fieldProps) => (
                <PasswordInput
                  {...fieldProps}
                  {...passwordForm.register('current_password')}
                  autoComplete="current-password"
                  disabled={passwordForm.formState.isSubmitting}
                />
              )}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="New Password" error={passwordForm.formState.errors.new_password?.message}>
                {(fieldProps) => (
                  <PasswordInput
                    {...fieldProps}
                    {...passwordForm.register('new_password')}
                    autoComplete="new-password"
                    disabled={passwordForm.formState.isSubmitting}
                  />
                )}
              </Field>
              <Field
                label="Confirm Password"
                error={passwordForm.formState.errors.confirm_password?.message}
              >
                {(fieldProps) => (
                  <PasswordInput
                    {...fieldProps}
                    {...passwordForm.register('confirm_password')}
                    autoComplete="new-password"
                    disabled={passwordForm.formState.isSubmitting}
                  />
                )}
              </Field>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                loading={passwordForm.formState.isSubmitting}
                disabled={!passwordForm.formState.isDirty}
              >
                Update Password
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}
